/**
 * Comprehensive Entity Explorer
 * Upgraded with modular components and advanced features
 * Supports both dashboard view (entity counts) and detailed table view
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { 
  Database, 
  Download, 
  Network,
  FileCode,
} from 'lucide-react';
import { exportEntities, exportAuditTrail } from '../../api/entities';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useEntityConfig } from './EntityConfigProvider';
import { EntityCountCards, EntityCount } from './EntityCountCards';
import { EntityDetailsModal } from './EntityDetailsModal';
import { EntityKnowledgeGraph } from './EntityKnowledgeGraph';
import { EntitySchemaDiagram } from './EntitySchemaDiagram';
import { EntityDBMLView } from './EntityDBMLView';
import { AuditTrailViewer } from './AuditTrailViewer';
import { getAPIClient } from '../../api/client';
import { exportToJSON } from '../../utils/export';

export interface EntityExplorerProps {
  /** Optional: Filter to specific entity types (if not provided, uses all from config) */
  entityTypes?: string[];
}

export function EntityExplorer({ entityTypes: propEntityTypes }: EntityExplorerProps = {}) {
  const { projectId } = useWorkspace();
  const { config: entityConfig, getEntityLabel, getEntityCategory } = useEntityConfig();
  
  const ENTITY_TYPES = propEntityTypes || entityConfig.availableTypes;
  
  // View mode: 'dashboard', 'table', 'graph', or 'dbml'
  const [viewMode, setViewMode] = useState<'dashboard' | 'table' | 'graph' | 'dbml'>('dashboard');
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null);
  const [entityCounts, setEntityCounts] = useState<EntityCount[]>([]);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [totalEntities, setTotalEntities] = useState(0);
  
  // Entity details for modal
  const [entityDetails, setEntityDetails] = useState<Array<{ id: string; data: any; source_artifact_id?: string }>>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // All entities for graph view
  const [allEntities, setAllEntities] = useState<Array<{ id: string; entity_type: string; data: any; source_artifact_id?: string }>>([]);
  const [loadingGraph, setLoadingGraph] = useState(false);
  
  // Audit trail state
  const [auditTrailData, setAuditTrailData] = useState<any>(null);
  const [showAuditTrailViewer, setShowAuditTrailViewer] = useState(false);
  const [exportingAuditTrail, setExportingAuditTrail] = useState(false);

  const handleEntityClick = useCallback(async (entityType: string) => {
    if (!projectId) return;
    
    setSelectedEntityType(entityType);
    setLoadingDetails(true);
    setViewMode('table');
    
    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('type', entityType);
    window.history.pushState({}, '', url.toString());
    
    try {
      const apiClient = getAPIClient();
      // Use bulk query even for single type (more efficient)
      const response = await apiClient.post('/entities/query-bulk', {
        project_id: projectId,
        entity_types: [entityType],
        limit_per_type: 10000,
      });
      
      // Extract entities from bulk response
      const result = response.results.find((r: any) => r.entity_type === entityType);
      setEntityDetails(result?.entities || []);
    } catch (error) {
      console.error(`Error fetching ${entityType} details:`, error);
      setEntityDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  }, [projectId]);

  // Read initial state from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type && ENTITY_TYPES.includes(type)) {
      setSelectedEntityType(type);
      setViewMode('table');
      handleEntityClick(type);
    }
  }, [ENTITY_TYPES, handleEntityClick]);

  // Fetch all entities for graph and DBML views
  useEffect(() => {
    if (!projectId || (viewMode !== 'graph' && viewMode !== 'dbml')) return;
    
    const fetchAllEntities = async () => {
      setLoadingGraph(true);
      const allEntitiesList: Array<{ id: string; entity_type: string; data: any; source_artifact_id?: string }> = [];
      const apiClient = getAPIClient();
      
      // Use bulk query endpoint for all entity types at once
      try {
        const bulkResponse = await apiClient.post('/entities/query-bulk', {
          project_id: projectId,
          entity_types: ENTITY_TYPES,
          limit_per_type: 200, // Sample entities for field analysis
        });
        
        const results = bulkResponse.results.map((result: any) => ({
          entities: result.entities.map((e: any) => ({
            ...e,
            entity_type: result.entity_type,
          })),
          totalCount: result.total_count || result.returned_count,
          entityType: result.entity_type,
        }));
        
        const entityCountsMap: Record<string, number> = {};
        
        results.forEach(result => {
          allEntitiesList.push(...result.entities);
          entityCountsMap[result.entityType] = result.totalCount;
        });
        
        setAllEntities(allEntitiesList);
        setEntityCounts(Object.entries(entityCountsMap).map(([type, count]) => ({
          type,
          label: getEntityLabel(type),
          count,
          category: getEntityCategory(type),
        })));
        setLoadingGraph(false);
      } catch (error) {
        console.error('Error fetching entities for graph:', error);
        setLoadingGraph(false);
      }
    };
    
    fetchAllEntities();
  }, [projectId, viewMode, ENTITY_TYPES, getEntityLabel, getEntityCategory]);

  // Compute connections for DBML view using the same logic as EntitySchemaDiagram
  const connections = useMemo(() => {
    if (allEntities.length === 0) return [];
    
    // Import the analyzeConnections function logic (inline for now)
    // Group entities by type
    const entitiesByType: Record<string, Array<{ id: string; data: any }>> = {};
    allEntities.forEach(e => {
      if (!entitiesByType[e.entity_type]) {
        entitiesByType[e.entity_type] = [];
      }
      entitiesByType[e.entity_type].push(e);
    });

    const entityTypes = Object.keys(entitiesByType);
    const connections: Array<{
      sourceType: string;
      targetType: string;
      connectingFields: string[];
      confidence: number;
    }> = [];

    const linkingPatterns = [
      { pattern: /^location_id$/, priority: 10 },
      { pattern: /^sample_id$/, priority: 10 },
      { pattern: /^well_id$/, priority: 10 },
      { pattern: /^site_id$/, priority: 9 },
      { pattern: /^coa_number$/, priority: 9 },
      { pattern: /^lab_sample_id$/, priority: 9 },
      { pattern: /^(.+)_id$/, priority: 7 },
    ];

    // Find common fields across all entity types
    const fieldPresence = new Map<string, Set<string>>();
    const fieldValues = new Map<string, Map<string, Set<string>>>();

    entityTypes.forEach(type => {
      const entities = entitiesByType[type] || [];
      const sample = entities.slice(0, Math.min(200, entities.length));
      const typeFields = new Map<string, Set<string>>();
      
      sample.forEach(e => {
        Object.keys(e.data || {}).forEach(field => {
          if (!typeFields.has(field)) typeFields.set(field, new Set());
          const value = String(e.data?.[field] || '').trim();
          if (value) typeFields.get(field)!.add(value);
        });
      });
      
      typeFields.forEach((values, field) => {
        if (!fieldPresence.has(field)) fieldPresence.set(field, new Set());
        fieldPresence.get(field)!.add(type);
        
        if (!fieldValues.has(type)) fieldValues.set(type, new Map());
        fieldValues.get(type)!.set(field, values);
      });
    });

    // Find common linking fields
    const commonFields = new Map<string, string[]>();
    fieldPresence.forEach((types, field) => {
      if (types.size >= 2) {
        let priority = 0;
        for (const { pattern, priority: p } of linkingPatterns) {
          if (pattern.test(field)) {
            priority = p;
            break;
          }
        }
        if (priority === 0 && (field.includes('_id') || field.includes('_uuid'))) {
          priority = 3;
        }
        if (priority > 0) {
          commonFields.set(field, Array.from(types));
        }
      }
    });

    // Create connections for all pairs that share common fields
    for (let i = 0; i < entityTypes.length; i++) {
      for (let j = i + 1; j < entityTypes.length; j++) {
        const typeA = entityTypes[i];
        const typeB = entityTypes[j];
        
        const sharedFields: Array<{ field: string; priority: number }> = [];
        commonFields.forEach((types, field) => {
          if (types.includes(typeA) && types.includes(typeB)) {
            let priority = 0;
            for (const { pattern, priority: p } of linkingPatterns) {
              if (pattern.test(field)) {
                priority = p;
                break;
              }
            }
            if (priority === 0 && (field.includes('_id') || field.includes('_uuid'))) {
              priority = 3;
            }
            if (priority > 0) {
              sharedFields.push({ field, priority });
            }
          }
        });
        
        sharedFields.sort((a, b) => b.priority - a.priority);
        const topFields = sharedFields.slice(0, 3).map(c => c.field);
        
        if (topFields.length > 0) {
          connections.push({
            sourceType: typeA,
            targetType: typeB,
            connectingFields: topFields,
            confidence: 0.8,
          });
        }
      }
    }

    return connections;
  }, [allEntities]);

  // Fetch entity counts for dashboard view
  useEffect(() => {
    if (!projectId || viewMode !== 'dashboard') return;
    
    const fetchCounts = async () => {
      setLoadingCounts(true);
      const counts: EntityCount[] = [];
      let total = 0;

      const apiClient = getAPIClient();
      
      // Use bulk query endpoint for all entity types at once
      try {
        const bulkResponse = await apiClient.post('/entities/query-bulk', {
          project_id: projectId,
          entity_types: ENTITY_TYPES,
          limit_per_type: 1, // Just check if any exist
        });

        const results = bulkResponse.results.map((result: any) => {
          const count = result.total_count || 0;
          if (count > 0) {
            return {
              type: result.entity_type,
              label: getEntityLabel(result.entity_type),
              count,
              category: getEntityCategory(result.entity_type),
            };
          }
          return null;
        }).filter((r: any) => r !== null);

        results.forEach(result => {
          if (result) {
            counts.push(result);
            total += result.count;
          }
        });

        setEntityCounts(counts);
        setTotalEntities(total);
        setLoadingCounts(false);
      } catch (error) {
        console.error('Error fetching entity counts:', error);
        setEntityCounts([]);
        setTotalEntities(0);
        setLoadingCounts(false);
      }
      results.forEach(result => {
        if (result) {
          counts.push(result);
          total += result.count;
        }
      });

      setEntityCounts(counts);
      setTotalEntities(total);
      setLoadingCounts(false);
    };

    fetchCounts();
  }, [projectId, viewMode, ENTITY_TYPES, getEntityLabel, getEntityCategory]);


  const closeModal = () => {
    setSelectedEntityType(null);
    setEntityDetails([]);
    setViewMode('dashboard');
    
    // Clear URL param
    const url = new URL(window.location.href);
    url.searchParams.delete('type');
    window.history.pushState({}, '', url.toString());
  };

  const exportMutation = useMutation({
    mutationFn: async (format: 'csv' | 'json' | 'xlsx', template?: string) => {
      if (!projectId || !selectedEntityType) return;
      
      // Use server-side export (app-specific templates handled by apps)
      const blob = await exportEntities(projectId, selectedEntityType, {}, format, template);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedEntityType}_export.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });

  // Note: Export templates are handled by app-specific implementations
  // Core EntityExplorer provides basic CSV/JSON/Excel export

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-theme-text-secondary">Select a project to explore entities</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-theme-text">Entity Explorer</h1>
            <p className="text-theme-text-secondary mt-1">
              {viewMode === 'dashboard' 
                ? `Total Entities: ${totalEntities.toLocaleString()}`
                : 'Query and manage structured entities'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {/* View Mode Toggle */}
          <div className="flex gap-1 border border-theme-border rounded-md p-1 bg-theme-elevated">
            <Button
              size="sm"
              variant={viewMode === 'dashboard' ? 'primary' : 'ghost'}
              onClick={() => {
                setViewMode('dashboard');
                setSelectedEntityType(null);
              }}
            >
              <Database className="h-4 w-4 mr-2" />
              Dashboard
          </Button>
            <Button
              size="sm"
              variant={viewMode === 'graph' ? 'primary' : 'ghost'}
              onClick={() => setViewMode('graph')}
            >
              <Network className="h-4 w-4 mr-2" />
              Graph
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'dbml' ? 'primary' : 'ghost'}
              onClick={() => setViewMode('dbml')}
            >
              <FileCode className="h-4 w-4 mr-2" />
              DBML
            </Button>
          </div>
          
          {viewMode === 'table' && selectedEntityType && (
            <>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => exportMutation.mutate('csv')}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                size="sm" 
                onClick={() => exportMutation.mutate('xlsx')}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={async () => {
                  if (!projectId || !selectedEntityType) return;
                  setExportingAuditTrail(true);
                  try {
                    const entityIds = entityDetails.map(e => e.id);
                    const auditTrail = await exportAuditTrail(projectId, selectedEntityType, entityIds);
                    setAuditTrailData(auditTrail);
                    setShowAuditTrailViewer(true);
                  } catch (error) {
                    console.error('Audit trail export failed:', error);
                  } finally {
                    setExportingAuditTrail(false);
                  }
                }}
                disabled={exportingAuditTrail || entityDetails.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                {exportingAuditTrail ? 'Exporting...' : 'Audit Trail'}
              </Button>
            </>
          )}
          {viewMode === 'dashboard' && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={async () => {
                if (!projectId) return;
                setExportingAuditTrail(true);
                try {
                  // Export audit trail for all entity types
                  const allEntityTypes = entityCounts.map(ec => ec.type);
                  const auditTrail = await exportAuditTrail(projectId, allEntityTypes);
                  setAuditTrailData(auditTrail);
                  setShowAuditTrailViewer(true);
                } catch (error) {
                  console.error('Audit trail export failed:', error);
                } finally {
                  setExportingAuditTrail(false);
                }
              }}
              disabled={exportingAuditTrail || entityCounts.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportingAuditTrail ? 'Exporting...' : 'Export Audit Trail'}
            </Button>
          )}
          {viewMode === 'table' && (
            <Button size="sm" variant="outline" onClick={closeModal}>
              Back to Dashboard
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard View */}
      {viewMode === 'dashboard' && (
        <>
          {loadingCounts ? (
      <Card>
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            </Card>
          ) : (
            <EntityCountCards
              entityCounts={entityCounts}
              onEntityClick={handleEntityClick}
              emptyMessage="No entities found. Please run ingest jobs first."
            />
          )}
        </>
      )}

      {/* Graph View */}
      {viewMode === 'graph' && (
        <Card>
          {loadingGraph ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <EntitySchemaDiagram
              entities={allEntities}
              entityLabels={Object.fromEntries(
                ENTITY_TYPES.map(type => [type, getEntityLabel(type)])
              )}
              entityCategories={Object.fromEntries(
                ENTITY_TYPES.map(type => [type, getEntityCategory(type)])
              )}
              entityCounts={Object.fromEntries(
                entityCounts.map(ec => [ec.type, ec.count])
              )}
              onEntityTypeClick={handleEntityClick}
            />
          )}
        </Card>
      )}

      {/* DBML View */}
      {viewMode === 'dbml' && (
        <>
          {loadingGraph ? (
            <Card>
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            </Card>
          ) : (
            <EntityDBMLView
              entities={allEntities}
              entityLabels={Object.fromEntries(
                ENTITY_TYPES.map(type => [type, getEntityLabel(type)])
              )}
              connections={connections}
            />
          )}
        </>
      )}

      {/* Entity Details Modal */}
      {selectedEntityType && viewMode === 'table' && (
        <EntityDetailsModal
          entityType={selectedEntityType}
          entityLabel={getEntityLabel(selectedEntityType)}
          entities={entityDetails}
          loading={loadingDetails}
          onClose={closeModal}
        />
      )}
      
      {/* Audit Trail Viewer */}
      {showAuditTrailViewer && auditTrailData && (
        <AuditTrailViewer
          data={auditTrailData}
          onClose={() => {
            setShowAuditTrailViewer(false);
            setAuditTrailData(null);
          }}
          onExport={() => {
            const typesLabel = Array.isArray(auditTrailData.export_metadata?.entity_types)
              ? auditTrailData.export_metadata.entity_types.length === 1
                ? auditTrailData.export_metadata.entity_types[0]
                : `${auditTrailData.export_metadata.entity_types.length}_types`
              : 'all_types';
            const auditFilename = `audit_trail_${typesLabel}_${new Date().toISOString().split('T')[0]}.json`;
            exportToJSON(auditTrailData, auditFilename, true);
          }}
        />
      )}
    </div>
  );
}
